import { inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AuthStateService } from './auth-state.service';

/**
 * Sincroniza tokens de acceso entre pestañas vía BroadcastChannel.
 * Cuando una pestaña refresca su token (vía el auth.interceptor en 401),
 * las demás pestañas reciben el nuevo token y actualizan su estado.
 *
 * El refresh proactivo se delegó al auth.interceptor, que maneja los 401s
 * reactivamente con deduplicación vía ReplaySubject. Esto evita colisiones
 * con el rate-limiting del backend (429).
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshService implements OnDestroy {
	private readonly authState = inject(AuthStateService);
	private readonly platformId = inject(PLATFORM_ID);

	private channel: BroadcastChannel | null = null;

	constructor() {
		if (isPlatformBrowser(this.platformId)) {
			try {
				this.channel = new BroadcastChannel('ventaclick-auth');
				this.channel.onmessage = (event: MessageEvent) => {
					const data = event.data as { type: string; accessToken: string } | undefined;
					if (data?.type === 'TOKEN_UPDATED' && data.accessToken) {
						const currentToken = this.authState.accessToken();
						if (currentToken !== data.accessToken) {
							this.authState.setAccessToken(data.accessToken);
						}
					}
				};
			} catch {
				// BroadcastChannel no soportado en este navegador
			}
		}
	}

	broadcastToken(accessToken: string): void {
		this.channel?.postMessage({ type: 'TOKEN_UPDATED', accessToken });
	}

	ngOnDestroy(): void {
		this.channel?.close();
	}
}
